import { motion, useAnimate, useReducedMotion } from "motion/react"
import { type FormEvent, useState } from "react"
import { useLocation, useNavigate } from "react-router"
import { api, ErrorApi } from "@/api/cliente"
import { BotonEntrar } from "@/componentes/acceso/BotonEntrar"
import { CampoAcceso } from "@/componentes/acceso/CampoAcceso"
import { IntroMarca } from "@/componentes/marca/IntroMarca"
import { type UsuarioSesion, usarSesion } from "@/lib/sesion"
import { LAMINA_SESION, SALIDA_ACCESO, VIAJE_LAMINA } from "@/lib/transiciones"
import {
  ES_PORTAL_TRANSPORTE,
  NOMBRE_PORTAL_TRANSPORTE,
  NOMBRE_PRODUCTO,
  VERSION_PRODUCTO,
} from "@/lib/version"

/**
 * La pantalla de acceso: el puerto en video a sangre (`FondoPuerto`) y una lámina
 * de cristal encima con la marca y el formulario.
 *
 * ⚠ **La lámina es el MISMO cristal oscuro que el marco del sistema**, y eso hace
 * dos cosas a la vez. La primera es que la transición no tiene costura: la tarjeta
 * se convierte en la barra lateral, y siendo el mismo material no hay color que
 * interpolar al aterrizar. La segunda es que deja ver el puerto moverse detrás, que
 * era el motivo de ponerla ahí.
 *
 * ⚠ **El logotipo es VECTORIAL y sin color propio.** Esto pasó por tres soluciones
 * antes: el PNG del manual, que desaparecía sobre el cristal; una pastilla blanca
 * detrás, que se leía como un parche pegado; y un segundo PNG aclarado, que eran
 * dos archivos de la misma marca con uno siempre a punto de quedarse viejo. Con un
 * SVG que hereda el color, lo pone quien lo usa — y no hay copia que mantener.
 *
 * ⚠ **El formulario NO espera a la animación de la marca.** La secuencia dura 5,6 s
 * y quien entra aquí lo hace veinte veces al día: los campos están enfocables y
 * escribibles desde el primer fotograma.
 *
 * ⚠ **La lámina es UNA, no dos.** Marca y formulario en tarjetas separadas sobre
 * fotografía son dos parches; juntos son una superficie puesta encima, que es lo
 * que el borde y la sombra están diciendo.
 *
 * ⚠ **Centrada.** Estuvo alineada a la derecha para no pisar el pie del carrusel
 * de fotos, y en un monitor ancho la pantalla quedaba descentrada sin que nada lo
 * explicara. El carrusel —y su fila de puntos— se fue con el video (11/10/2026),
 * así que tampoco queda hueco que reservar abajo.
 *
 * ⚠ **Los estados del envío se VEN** (11/10/2026): destello y puntos al pulsar
 * (`BotonEntrar`), la tarjeta que niega con la cabeza si falla, y una palomita
 * antes del viaje si entra. Todo es `transform`/`opacity` y todo se apaga con
 * `prefers-reduced-motion`.
 *
 * ⚠ **Se construye desde 320 px.** Cada medida de esta pantalla tiene su variante
 * estrecha; ver `.claude/rules/frontend.md`. La resta que manda es: ancho de
 * pantalla − relleno del contenedor − relleno de la lámina.
 */
export function Login() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)
  // Credenciales aceptadas y ya navegando. Sirve para apagar el contenido de la
  // tarjeta ANTES de que la lámina se ponga en marcha.
  const [saliendo, setSaliendo] = useState(false)
  // Credenciales aceptadas: la palomita del botón, antes del viaje.
  const [entrado, setEntrado] = useState(false)
  const quieto = useReducedMotion()
  // La tarjeta niega con la cabeza al fallar: sólo `x`, y en un envoltorio propio
  // para no tocar ni la entrada (`y`) ni la lámina, que lleva el `layoutId`.
  const [tarjeta, animarTarjeta] = useAnimate<HTMLDivElement>()

  const setUsuario = usarSesion((e) => e.setUsuario)
  const navegar = useNavigate()
  const ubicacion = useLocation()
  const destino = (ubicacion.state as { desde?: string } | null)?.desde ?? "/"

  async function enviar(evento: FormEvent) {
    evento.preventDefault()
    setError(null)
    setEnviando(true)
    try {
      await api.post("/api/v1/auth/login", { email, password })
      const usuario = await api.get<UsuarioSesion>("/api/v1/auth/yo")

      // ⚠ **Se apaga el contenido y SÓLO ENTONCES se navega.** Sin esta pausa, el
      // formulario y la marca desaparecen en el mismo fotograma en que la lámina
      // empieza a moverse, y eso no se lee como una transformación: se lee como un
      // corte. La tarjeta no se desvanece —tiene que seguir ahí para viajar—; lo que
      // se apaga es lo que sobra durante el viaje.
      // La palomita se dibuja antes del viaje: confirma que lo que viene es el
      // sistema y no otro error. Sin movimiento no hay nada que esperar.
      setEntrado(true)
      if (!quieto) await new Promise((listo) => setTimeout(listo, 450))

      setSaliendo(true)
      await new Promise((listo) => setTimeout(listo, SALIDA_ACCESO * 1000))

      setUsuario(usuario)
      navegar(destino, { replace: true })
    } catch (e) {
      // El mensaje del servidor es genérico a propósito: no dice si el correo
      // existe. Se muestra tal cual — reescribirlo aquí sería deshacer eso.
      setError(e instanceof ErrorApi ? e.message : "No se pudo iniciar sesión")
      if (!quieto && tarjeta.current) {
        animarTarjeta(
          tarjeta.current,
          { x: [0, -7, 6, -4, 2, 0] },
          { duration: 0.36, ease: "easeInOut" },
        )
      }
    } finally {
      setEnviando(false)
    }
  }

  return (
    <div className="h-full overflow-y-auto">
      {/*
        `pointer-events-none` en la capa que envuelve al formulario, y el gesto
        devuelto sólo a la lámina. Esta capa cubre la pantalla entera para poder
        colocar, y sin esto se tragaría cualquier clic dirigido a lo que haya debajo.
      */}
      <div className="pointer-events-none relative flex min-h-full items-center justify-center p-4 sm:p-6 lg:p-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
          className="pointer-events-auto w-full max-w-md"
        >
          <div ref={tarjeta}>
            {/* ⚠ **`layoutId` compartido con la barra lateral.** Es lo que hace que al
              entrar esta tarjeta no desaparezca: se estrecha hasta el ancho del menú
              y se estira hasta el alto de la pantalla, convirtiéndose en él. Para
              que funcione, las dos tienen que estar bajo el mismo `LayoutGroup`
              —lo monta `main.tsx`— y el identificador tiene que ser LITERALMENTE el
              mismo. Si alguien renombra uno de los dos, no se rompe nada: la
              transición simplemente deja de ocurrir, y eso no lo caza ninguna
              compuerta. */}
            <motion.div
              layoutId={LAMINA_SESION}
              transition={VIAJE_LAMINA}
              className="lamina-cristal px-5 pt-5 pb-4 sm:px-8 sm:pt-7 sm:pb-6"
            >
              {/* Lo que se apaga durante el viaje. La lámina se queda; esto no. */}
              <motion.div
                animate={{ opacity: saliendo ? 0 : 1 }}
                transition={{ duration: SALIDA_ACCESO, ease: "easeOut" }}
              >
                <IntroMarca
                  producto={ES_PORTAL_TRANSPORTE ? NOMBRE_PORTAL_TRANSPORTE : NOMBRE_PRODUCTO}
                  className="mb-5 sm:mb-6"
                />

                <form onSubmit={enviar} className="space-y-4">
                  <CampoAcceso
                    id="email"
                    rotulo="Correo"
                    tipo="email"
                    autocompletar="username"
                    valor={email}
                    onCambio={setEmail}
                    invalido={Boolean(error)}
                  />
                  <CampoAcceso
                    id="password"
                    rotulo="Contraseña"
                    tipo="password"
                    autocompletar="current-password"
                    valor={password}
                    onCambio={setPassword}
                    invalido={Boolean(error)}
                  />

                  {error && (
                    // `role="alert"` para que un lector de pantalla lo anuncie: el error
                    // es texto, no sólo un borde rojo (§15). En rosa CLARO y no en el
                    // `--destructive` del tema: aquél está elegido contra una superficie
                    // blanca, y sobre este cristal oscuro se lee como una mancha.
                    <motion.p
                      role="alert"
                      initial={quieto ? false : { opacity: 0, y: -6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                      className="rounded-[var(--radius-control)] border border-rose-300/40 bg-rose-500/25 px-3 py-2 text-sm text-rose-50"
                    >
                      {error}
                    </motion.p>
                  )}

                  <BotonEntrar estado={entrado ? "ok" : enviando ? "enviando" : "reposo"} />
                </form>

                {/* La versión, al pie y en pequeño: es lo primero que se pregunta cuando
                alguien reporta un fallo, y lo último que hay que ir a buscar. */}
                <p className="mt-5 text-center text-[0.6875rem] tracking-wide text-[rgb(var(--marco-tinta)/0.55)]">
                  Versión {VERSION_PRODUCTO}
                </p>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
